<template>
  <div :class="[{ divider: props.divider }, twMerge('etg-collapse mt-4', props.containerClass)]">
    <n-collapse
      v-bind="$attrs"
      arrow-placement="right"
      :default-expanded-names="props.defaultExpandedName ? [props.defaultExpandedName] : undefined"
      @item-header-click="handleItemHeaderClick"
    >
      <template v-for="item in props.list" :key="item.title">
        <n-collapse-item v-if="item.content || item.custom" :name="item.title">
          <template #arrow><div class="placeholder"></div></template>
          <template #header="{ collapsed }">
            <div :class="twMerge('flex items-start justify-between flex-1 text-lg gap-2 pb-3 cursor-pointer', props.headerClass)">
              <div :class="twMerge('py-1', props.titleClass, expandedName === item.title ? `text-${props.expandedColor}` : undefined)">
                {{ item.title }}
              </div>
              <svg-icon
                name="arrow-up"
                :color="expandedName === item.title ? props.expandedColor || props.iconColor : props.iconColor"
                :class="[
                  { 'rotate-180': collapsed },
                  `size-${props.iconSize}`,
                  twMerge('p-1.5 flex-shrink-0 transition-transform transform', props.iconClass),
                ]"
              />
            </div>
          </template>
          <slot name="content" :item="item">
            <div class="flex flex-col content-wrapper text-base pb-4">
              <p :class="props.contentClass" v-html="item.content"></p>
            </div>
          </slot>
        </n-collapse-item>
      </template>
    </n-collapse>
  </div>
</template>

<script setup lang="ts" generic="T extends CollapseItem">
import { twMerge } from 'tailwind-merge'
import { NCollapse, NCollapseItem, type CollapseProps } from 'naive-ui'

export interface CollapseItem extends Record<string, unknown> {
  title: string
  content?: string
  custom?: boolean
}

interface Props {
  list: T[]
  divider?: boolean
  defaultExpandedName?: string
  expandedColor?: string
  containerClass?: string
  headerClass?: string
  titleClass?: string
  contentClass?: string
  iconColor?: string
  iconSize?: number
  iconClass?: ClassType
}

const props = withDefaults(defineProps<Props>(), {
  divider: false,
  iconSize: 8,
})

const expandedName = ref(props.defaultExpandedName)

const handleItemHeaderClick: CollapseProps['onItemHeaderClick'] = ({ name, expanded }) => {
  if (expanded) {
    expandedName.value = name
  }
}
</script>

<style lang="scss" scoped>
.etg-collapse {
  .content-wrapper {
    :deep(a) {
      @apply text-red-50;
    }
  }

  &.divider {
    :deep(.n-collapse) {
      .n-collapse-item:not(:last-child) {
        @apply border-t-0 border-b border-gray-40;
      }
    }
  }
}

:deep(.n-collapse) {
  .n-collapse-item {
    @apply border-0 m-0;

    .n-collapse-item__header {
      .n-collapse-item-arrow,
      .n-collapse-item__header-extra {
        @apply hidden;
      }
    }
  }
}
</style>
